import { reactive, getCurrentInstance, computed } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { useEmployees } from "@/composables/employees/useEmployees";
import { usePositions } from "@/composables/positions/usePositions";
import { useReadinessOrganization } from "@/composables/readinessOrganization/useReadinessOrganization";
import { transformChatterData } from "@/utils/chatterUtils";
let instance = null;

export function useViewState() {
  if (instance) return instance;
  const filter = useFilters();
  const employee = useEmployees();
  const position = useReadinessOrganization();
  const positions = usePositions();
  const app = getCurrentInstance();
  const { proxy } = app || {};
  const state = reactive({
    filters: {
      values: [],
      searchText: "",
      showFilterDialog: false,
      key: "readinessEmployees",
      clearSelection: "",
      filterGroups: [],
    },
    loading: {
      table: false,
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalPositionChatter: 0,
      page: 1,
      positionId: null,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    readiness: {
      average_readiness: 0,
      loading: false,
    },
  });

  const resetPagination = () => {
    state.table.currentPage = 1;
  };

  // Initialize view state - reset pagination and search on component mount
  const initializeViewState = () => {
    resetPagination();
    clearSearchText();
  };

  const applyFilters = async (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    state.filters.showFilterDialog = false;
    // Reset pagination when filters are applied
    resetPagination();
    await loadPositionsWithFilters();
  };

  const removeFilters = (removedFilters) => {
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;
  };

  const currentEmployee = computed(
    () => state.assignmentWizard.currentEmployee || state.relieveAssignment.currentEmployee
  );
  const getReadinessOrgOccupationTagProp = (item) => {
    return {
      occupied_by: item.assigned_primary,
      firstName: item?.assigned_primary?.assigned_employee_name?.split(" ")[0] || "",
      lastName: item?.assigned_primary?.assigned_employee_name?.split(" ").slice(1).join(" ") || "",
      photoUrl: item?.assigned_primary?.assigned_employee_avatar,
      availability: item?.assigned_primary?.assigned_employee_availability || 1,
      showAvailability: true,
      nameUrl: `/organization/employees/${item?.assigned_primary?.assigned_employee_id}/pathtoreadiness`,
      showLinkUnderline: false,
      hideAvailabilitySelector: true,
      isAllocated: true,
    };
  };
  const getPositionReadinessProps = (item) => {
    return {
      recalc: false,
      readiness: item.readiness || 0,
      status: "Has Assignments",
    };
  };
  const getSwitcherProps = (item) => {
    return {
      label: "",
      value: item.exclude_from_avg || false,
      left: "-25px",
      toggleLoader: state.readiness.loading,
    };
  };

  // Métodos para filtros
  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const clearFilters = () => {
    // ER-11195: "Clear all" restores the documented defaults from the central
    // registry instead of resetting the whole filters object to a legacy shape.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    state.filters.showFilterDialog = false;
  };
  const clearSearchText = () => {
    state.filters.searchText = "";
  };
  const getPositionCode = (position) => {
    if (position?.code_str) {
      const match = position.code_str.match(/#\d+/);
      return match ? match[0] : "";
    }
    return "#";
  };

  // Métodos para loading
  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };
  const getPositionChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await positions.positionChatter(
        accountID,
        state.openChatterModal.positionId,
        "position",
        state.openChatterModal.page
      );
      if (response) {
        const chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalPositionChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };
  const openPositionChatterModal = async (item) => {
    positions.setCurrentPosition(item);
    state.openChatterModal.chatterData = [];
    state.openChatterModal.positionId = item.id;
    await getPositionChatter();
  };
  const closePositionChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalPositionChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };
  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getPositionChatter();
  };
  const loadAccountDetails = async () => {
    state.readiness.loading = true;
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await employee.getAccountDetails(accountID);
      if (response) {
        setTimeout(() => {
          state.readiness.average_readiness = response.data.avg_position_readiness;
          state.readiness.loading = false;
        }, 1000);
      } else {
        state.readiness.loading = false;
      }
    } catch (error) {
      console.error("Error loading account details:", error);
      state.readiness.loading = false;
    }
  };
  const updatePositionExcludeFromAvgValue = async (item, val) => {
    state.readiness.loading = true;
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const payload = {
      position_id: item.id,
      value: val.value,
    };
    await positions.updatePositionExcludeFromAvg(accountID, payload);
    await loadAccountDetails();
    await loadPositionsWithFilters();
    state.readiness.loading = false;
  };

  const recalculateAllEmployees = async () => {
    proxy.$loader.show();
    try {
      // Reset pagination to first page when recalculating
      state.table.currentPage = 1;
      await loadPositionsWithFilters();
    } finally {
      proxy.$loader.hide();
    }
  };
  const openPositionDetails = async (position) => {
    await proxy.$router.push(`/organization/positions/position/${position.id}`);
  };
  // Load employees with current filters
  const loadPositionsWithFilters = async () => {
    proxy.$loader.show();
    const queryParams = {};

    // Add pagination parameters
    queryParams.limit = state.table.itemsPerPage;
    queryParams.offset = (state.table.currentPage - 1) * state.table.itemsPerPage;

    // Add search parameter for first_name
    if (state.filters.searchText && state.filters.searchText.trim()) {
      queryParams.name = state.filters.searchText.trim();
    }

    // Add ordering
    queryParams.ordering = "name";

    // Add archived filter to exclude archived employees
    queryParams.archived = false;

    await position.loadAllPositions(queryParams);

    // Update the employee store's pagination state to match our viewState
    const positionStore = positions.positionStore;
    if (positionStore) {
      positionStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: positionStore.getTotalPositionsCount,
      });
    }
    proxy.$loader.hide();
  };
  // Debounce function for search
  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (searchText) => {
    // Always clear existing search filters first
    if (state.filters.searchText) {
      // Clear previous search
      state.filters.searchText = "";
    }

    // Only add search filters and load employees if there's actually a search term
    if (searchText && searchText.trim() !== "") {
      state.filters.searchText = searchText.trim();
    }

    // Reset to first page when searching
    resetPagination();

    // Load employees with current filters (including search)
    await loadPositionsWithFilters();
  };

  // Debounced search function
  const debouncedSearch = debounce(performSearch, 500);

  // Handle search input
  const handleSearch = (searchText) => {
    debouncedSearch(searchText);
  };

  instance = {
    state,
    currentEmployee,
    updateFilter,
    clearFilters,
    setLoading,
    getPositionReadinessProps,
    applyFilters,
    removeFilters,
    openPositionChatterModal,
    closePositionChatterModal,
    requestMoreChatter,
    getSwitcherProps,
    loadAccountDetails,
    updatePositionExcludeFromAvgValue,
    recalculateAllEmployees,
    getPositionCode,
    getReadinessOrgOccupationTagProp,
    openPositionDetails,
    loadPositionsWithFilters,
    handleSearch,
    initializeViewState,
    clearSearchText,
  };
  return instance;
}
